/**
 * ApprovalWorkspace — sequential-chain approvals surface that replaces the
 * legacy Kanban-by-decision view.
 *
 * State machine, branched on `evaluationStatus` + chain shape:
 *
 *   1. Pre-submit  (status DRAFT / IN_ANALYSIS / REJECTED, chain empty
 *                   or unsubmitted) → ApprovalChainBuilder + ReadinessChecklist
 *                   + Submit button (enabled only when readiness all-green
 *                   AND chain has ≥1 approver)
 *   2. In-flight   (status UNDER_REVIEW) → vertical timeline of approvers,
 *                   only the lowest-sequence PENDING node decidable, current
 *                   approver sees a Decide button → ApprovalDecideForm modal
 *   3. Terminal    (status APPROVED / REJECTED) → status banner + read-only
 *                   timeline so the audit trail stays visible
 *
 * Submit gating uses both ReadinessChecklist's all-blocking-pass logic and
 * the chain's existence (≥1 approver row in approvals.byEvaluation).
 *
 * Linear: MYD-203 (D4).
 */

import { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	UserCheck,
	Send,
	Loader2,
	AlertCircle,
	CheckCircle2,
	XCircle,
	RotateCcw,
} from 'lucide-react'

import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'

import {
	fetchApprovals,
	submitChain,
	type ReadinessSnapshot,
} from 'slices/approvalSlice'
import { fetchEvaluationById } from 'slices/evaluationSlice'

import { isAskMdtAvailable } from 'features/AskMdt/availability'
import { EvaluationDraftPanel } from 'features/AskMdt/components/EvaluationDraftPanel'

import ApprovalChainBuilder from './ApprovalChainBuilder'
import ReadinessChecklist from './ReadinessChecklist'
import ApprovalTimelineNode from './ApprovalTimelineNode'
import DecisionModal from './DecisionModal'

interface ApprovalWorkspaceProps {
	evalId: string
	evaluationStatus: string
}

const PRE_SUBMIT_STATUSES = new Set(['DRAFT', 'IN_ANALYSIS', 'REJECTED'])
const TERMINAL_STATUSES = new Set(['APPROVED', 'REJECTED', 'ARCHIVED'])

const ApprovalWorkspace = ({ evalId, evaluationStatus }: ApprovalWorkspaceProps) => {
	const dispatch = useDispatch()
	// "Draft approval memo" (MYD-639), where Ask MDT is offered.
	const askAvailable = isAskMdtAvailable(useSelector((state: any) => state.userLogin?.userInfo))

	const approvals: any[] = useSelector(
		(state: any) => state.approvals?.byEvaluation?.[evalId] || [],
	)
	const readiness: ReadinessSnapshot | undefined = useSelector(
		(state: any) => state.approvals?.readinessByEvaluation?.[evalId],
	)
	const currentUserId: string | null = useSelector(
		(state: any) => state.userLogin?.userInfo?._id ?? null,
	)

	const [decideOpen, setDecideOpen] = useState(false)
	const [decideTarget, setDecideTarget] = useState<any>(null)
	const [submitting, setSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState<string | null>(null)

	useEffect(() => {
		// @ts-ignore — thunk
		dispatch(fetchApprovals(evalId))
	}, [dispatch, evalId])

	// Sort by sequence so timeline + lowest-pending lookup are stable.
	const sortedApprovals = useMemo(
		() => approvals.slice().sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
		[approvals],
	)

	const currentSequence = useMemo(() => {
		const next = sortedApprovals.find((a) => a.decision === 'PENDING')
		return next?.sequence ?? null
	}, [sortedApprovals])

	const allBlockingPass = useMemo(() => {
		if (!readiness) return false
		// Mirrors ReadinessChecklist's reasoning — non-informational checks
		// pass iff `reasons` doesn't contain a blocking entry.
		return readiness.ready
	}, [readiness])

	const isPreSubmit = PRE_SUBMIT_STATUSES.has(evaluationStatus)
	const isInFlight = evaluationStatus === 'UNDER_REVIEW'
	const isTerminal = TERMINAL_STATUSES.has(evaluationStatus) && !isPreSubmit
	// Note: REJECTED is in both PRE_SUBMIT_STATUSES and TERMINAL_STATUSES.
	// The convention from D1: REJECTED means a reject decision came in.
	// Owner may rebuild + resubmit (chain editable), so treat as pre-submit.

	const canSubmit = allBlockingPass && approvals.length > 0 && !submitting

	const handleSubmit = useCallback(async () => {
		setSubmitError(null)
		setSubmitting(true)
		// @ts-ignore — thunk
		const result: any = await dispatch(submitChain(evalId))
		setSubmitting(false)
		if (result?.error) {
			setSubmitError(result.payload || 'Failed to submit chain')
			return
		}
		// Status flipped to UNDER_REVIEW server-side — refresh evaluation so the
		// outlet context picks up the new status and the workspace re-renders
		// in in-flight mode.
		// @ts-ignore — thunk
		dispatch(fetchEvaluationById(evalId))
	}, [dispatch, evalId])

	const handleDecide = useCallback((approval: any) => {
		setDecideTarget(approval)
		setDecideOpen(true)
	}, [])

	const handleDecideClose = useCallback(() => {
		setDecideOpen(false)
		setDecideTarget(null)
		// Re-fetch readiness in case CHANGES_REQUESTED bounced the evaluation back
		// to IN_ANALYSIS — readiness is still relevant for the resubmit path.
		// @ts-ignore — thunk
		dispatch(fetchApprovals(evalId))
	}, [dispatch, evalId])

	// ---------------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------------

	const heading = (
		<div>
			<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
				<UserCheck className="w-5 h-5 text-[#0080fe]" />
				Approvals
			</h3>
			<p className="text-sm text-gray-500">
				{isPreSubmit && 'Build the approval chain, confirm readiness, then submit for review.'}
				{isInFlight && 'Chain in flight. Each approver decides in sequence.'}
				{isTerminal && 'Final state. Timeline below preserves the audit trail.'}
			</p>
		</div>
	)

	// Terminal banner shown above the timeline in APPROVED / final REJECTED.
	// (REJECTED-as-resubmittable is treated as pre-submit, so doesn't reach here.)
	const terminalBanner = (() => {
		if (evaluationStatus === 'APPROVED') {
			return (
				<div className="flex items-start gap-2 px-4 py-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800">
					<CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
					<div className="text-sm">
						<div className="font-semibold">Evaluation approved.</div>
						<div className="text-xs mt-0.5">
							All approvers signed off. The chain below shows each decision in order.
						</div>
					</div>
				</div>
			)
		}
		if (evaluationStatus === 'ARCHIVED') {
			return (
				<div className="flex items-start gap-2 px-4 py-3 rounded-md bg-gray-50 border border-gray-200 text-gray-700 text-sm">
					Archived — no further actions available.
				</div>
			)
		}
		return null
	})()

	return (
		<div className="space-y-5">
			{heading}

			{/* PRE-SUBMIT: chain builder + readiness + submit */}
			{isPreSubmit && (
				<>
					{evaluationStatus === 'REJECTED' && (
						<div className="flex items-start gap-2 px-4 py-3 rounded-md bg-red-50 border border-red-200 text-red-800">
							<XCircle className="w-5 h-5 mt-0.5 shrink-0" />
							<div className="text-sm">
								<div className="font-semibold">Previously rejected.</div>
								<div className="text-xs mt-0.5">
									Adjust the chain or fix readiness items, then resubmit.
								</div>
							</div>
						</div>
					)}
					{evaluationStatus === 'IN_ANALYSIS' && approvals.length > 0 && (
						<div className="flex items-start gap-2 px-4 py-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
							<RotateCcw className="w-5 h-5 mt-0.5 shrink-0" />
							<div className="text-sm">
								<div className="font-semibold">Changes requested.</div>
								<div className="text-xs mt-0.5">
									Address feedback in the chain below, then resubmit. The chain is
									preserved.
								</div>
							</div>
						</div>
					)}

					<ApprovalChainBuilder evalId={evalId} />
					<ReadinessChecklist evalId={evalId} />
					{askAvailable && <EvaluationDraftPanel evaluationId={evalId} kind="memo" />}

					<Card>
						<CardContent className="pt-5">
							<div className="flex items-center justify-between gap-3 flex-wrap">
								<div className="min-w-0">
									<div className="text-sm font-semibold text-gray-900">
										Submit for approval
									</div>
									<div className="text-xs text-gray-500 mt-0.5">
										{!canSubmit && approvals.length === 0
											? 'Save the approval chain first.'
											: !canSubmit && !allBlockingPass
												? 'Fix the readiness items above first.'
												: 'Ready — this transitions the evaluation to UNDER_REVIEW and unlocks approver #1.'}
									</div>
									{submitError && (
										<div className="mt-2 flex items-start gap-1.5 text-xs text-red-600">
											<AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
											{submitError}
										</div>
									)}
								</div>
								<Button
									variant="primary"
									disabled={!canSubmit}
									onClick={handleSubmit}
									className="gap-1.5"
								>
									{submitting ? (
										<>
											<Loader2 className="w-4 h-4 animate-spin" />
											Submitting…
										</>
									) : (
										<>
											<Send className="w-4 h-4" />
											{evaluationStatus === 'IN_ANALYSIS' || evaluationStatus === 'REJECTED'
												? 'Resubmit'
												: 'Submit for review'}
										</>
									)}
								</Button>
							</div>
						</CardContent>
					</Card>
				</>
			)}

			{/* IN-FLIGHT: timeline of approvers */}
			{(isInFlight || isTerminal) && (
				<>
					{terminalBanner}

					<Card>
						<CardHeader className="pb-3">
							<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
								<UserCheck className="w-4 h-4" />
								Approval chain ({sortedApprovals.length})
							</h4>
						</CardHeader>
						<CardContent>
							{sortedApprovals.length === 0 ? (
								<div className="text-center py-6 text-sm text-gray-500 italic">
									No approvers on this evaluation.
								</div>
							) : (
								<div className="pt-1">
									{sortedApprovals.map((approval, idx) => (
										<ApprovalTimelineNode
											key={approval._id}
											approval={approval}
											isCurrent={
												isInFlight && approval.sequence === currentSequence
											}
											isLast={idx === sortedApprovals.length - 1}
											currentUserId={currentUserId}
											onDecide={handleDecide}
										/>
									))}
								</div>
							)}
						</CardContent>
					</Card>
				</>
			)}

			<DecisionModal
				open={decideOpen}
				onClose={handleDecideClose}
				evalId={evalId}
				approval={decideTarget}
			/>
		</div>
	)
}

export default ApprovalWorkspace
